import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Backdrop, Box, Button, Chip, CircularProgress, Collapse, Drawer, IconButton,
  InputAdornment, MenuItem, Checkbox, FormControlLabel,
  Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon, DeleteOutline as DeleteIcon,
  Close as CloseIcon, EditOutlined as EditIcon, PaidOutlined as PaidIcon,
  Search as SearchIcon, SettingsSuggestOutlined as ServicesIcon,
  Inventory2Outlined as PackageIcon, EventNoteOutlined as PlanIcon,
  GridViewOutlined as GridViewIcon, ViewListOutlined as ListViewIcon
} from '@mui/icons-material';
import {
  AddPackage, AddPlan, AddServicePrices, AddServices, DeletePackage, DeletePlan,
  DeleteServicePrices, DeleteServices, ListPackages, ListPlans, ListServicePrices,
  ListServices, UpdatePackage, UpdatePackageServices, UpdatePlan,
  UpdateServicePrices, UpdateServices
} from '../../util/API/feesbackend';
import './NewFeesModule.scss';

const emptyService = { id: null, name: '', description: '', unitlabel: '', metered: '1' };
const emptyPrice = { pricetype: 'on_demand', currency: 'USD', unitprice: '', effectivefrom: '', effectiveto: '' };
const emptyPackage = { name: '', description: '', serviceIds: [] };
const emptyPlan = {
  name: '',
  currency: 'USD',
  billingIntervalMonths: '1',
  price: '',
  active: '1',
  effectivefrom: '',
  effectiveto: '',
  newChargeMode: 'prorated',
  oldCreditMode: 'prorated'
};
const noDate = (value) => !value || value === '0000-00-00' ? '—' : value;

export default function NewFeesModule() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedServiceId, setExpandedServiceId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [serviceModal, setServiceModal] = useState({ open: false, mode: 'create', data: emptyService });
  const [priceModal, setPriceModal] = useState({ open: false, mode: 'create', serviceId: null, priceId: null, data: emptyPrice });
  const [activeWorkspace, setActiveWorkspace] = useState('services');
  const [packages, setPackages] = useState([]);
  const [packageSearch, setPackageSearch] = useState('');
  const [packageView, setPackageView] = useState('grid');
  const [packagePage, setPackagePage] = useState(0);
  const [packagesPerPage, setPackagesPerPage] = useState(8);
  const [packageDrawer, setPackageDrawer] = useState({ open: false, mode: 'create', packageId: null, data: emptyPackage });
  const [plansDrawer, setPlansDrawer] = useState({ open: false, packageId: null });
  const [planDrawer, setPlanDrawer] = useState({ open: false, mode: 'create', packageId: null, planId: null, data: emptyPlan });
  const [mutation, setMutation] = useState({ key: '', label: '' });
  const [alert, setAlert] = useState({ showAlert: false, message: '', severity: '', header: '', onConfirm: null, alertType: '' });

  // The dashboard is intentionally fetched once when this module opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDashboardData(); getPackages(); }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await ListServices();
      if (response?.status !== 'success' || !Array.isArray(response?.data)) {
        setServices([]);
        return;
      }
      const mappedServices = response.data.map((item) => ({
        id: String(item.id),
        name: item.name,
        description: item.description || '',
        unitlabel: item.unit_label || 'unit',
        metered: item.metered ? '1' : '0',
        prices: []
      }));
      setServices(mappedServices);
      await getServicePrices(mappedServices);
    } catch (error) {
      console.error('Fee API Error:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const getServicePrices = async (currentServices) => {
    try {
      const response = await ListServicePrices('');
      if (response?.status !== 'success' || !Array.isArray(response?.data)) return;
      const prices = response.data.map((item) => ({
        id: item.id,
        serviceId: String(item.service_id),
        pricetype: item.price_type,
        currency: item.currency,
        unitprice: item.unit_price,
        effectivefrom: noDate(item.effective_from),
        effectiveto: noDate(item.effective_to)
      }));
      setServices(currentServices.map(service => ({ ...service, prices: prices.filter(price => price.serviceId === service.id) })));
    } catch (error) {
      console.error('Fee API Error:', error);
    }
  };

  const getPackages = async () => {
    try {
      const [packageResponse, planResponse] = await Promise.all([ListPackages(), ListPlans()]);
      if (
        packageResponse?.status !== 'success'
        || !Array.isArray(packageResponse?.data)
        || planResponse?.status !== 'success'
        || !Array.isArray(planResponse?.data)
      ) {
        setPackages([]);
        return;
      }
      const plans = planResponse.data.map(item => ({
        id: String(item.id),
        packageId: String(item.package_id),
        name: item.name || '',
        currency: item.currency || 'USD',
        billingIntervalMonths: String(item.billing_interval_months || 1),
        price: item.price_recurring,
        active: item.active ? '1' : '0',
        effectivefrom: item.effective_from || '',
        effectiveto: item.effective_to || '',
        newChargeMode: item.default_new_charge_mode || 'prorated',
        oldCreditMode: item.default_old_credit_mode || 'prorated'
      }));
      setPackages(packageResponse.data.map(item => ({
        id: String(item.id),
        name: item.name || '',
        description: item.description || '',
        serviceIds: Array.isArray(item.service_ids) ? item.service_ids.map(String) : [],
        plans: plans.filter(plan => plan.packageId === String(item.id))
      })));
    } catch (error) {
      console.error('Package API Error:', error);
      setPackages([]);
    }
  };

  const showSuccess = (message) => setAlert({ showAlert: true, severity: 'success', message, header: 'Success' });
  const showApiError = (error, fallbackMessage) => setAlert({
    showAlert: true,
    severity: 'error',
    message: error?.response?.data?.message || fallbackMessage,
    header: 'Unable to complete request'
  });
  const confirmDelete = (message, action) => setAlert({
    showAlert: true, message, severity: 'confirm', header: 'Please confirm', alertType: 'confirm', onConfirm: action
  });
  const closeAlert = (_, reason) => {
    if (reason === 'clickaway') return;
    setAlert(previous => ({ ...previous, showAlert: false, onConfirm: null }));
  };
  const confirmAlertAction = () => {
    const action = alert.onConfirm;
    setAlert(previous => ({ ...previous, showAlert: false, onConfirm: null }));
    if (action) action();
  };
  const beginMutation = (key, label) => setMutation({ key, label });
  const endMutation = () => setMutation({ key: '', label: '' });
  const closeServiceModal = () => setServiceModal(previous => ({ ...previous, open: false }));
  const closePriceModal = () => setPriceModal(previous => ({ ...previous, open: false }));
  const closePackageDrawer = () => setPackageDrawer(previous => ({ ...previous, open: false }));
  const closePlansDrawer = () => setPlansDrawer(previous => ({ ...previous, open: false }));
  const closePlanDrawer = () => setPlanDrawer(previous => ({ ...previous, open: false }));

  const saveService = async () => {
    if (mutation.key) return;
    const { data, mode } = serviceModal;
    beginMutation('service-save', mode === 'create' ? 'Creating service…' : 'Updating service…');
    try {
      const response = mode === 'create'
        ? await AddServices(data.name, data.description, data.unitlabel, data.metered)
        : await UpdateServices(data.id, data.name, data.description, data.unitlabel, data.metered);
      if (response?.status === 'success') {
        closeServiceModal();
        await fetchDashboardData();
        showSuccess(mode === 'create' ? 'Service created successfully.' : 'Service updated successfully.');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      showApiError(error, 'The service could not be saved.');
    } finally {
      endMutation();
    }
  };

  const deleteService = async (id) => {
    if (mutation.key) return;
    beginMutation('service-delete', 'Deleting service…');
    try {
      const response = await DeleteServices(id);
      if (response?.status === 'success') {
        await fetchDashboardData();
        showSuccess('Service deleted successfully.');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      showApiError(error, 'The service could not be deleted.');
    } finally {
      endMutation();
    }
  };

  const savePrice = async () => {
    if (mutation.key) return;
    const { mode, serviceId, priceId, data } = priceModal;
    beginMutation('price-save', mode === 'create' ? 'Adding pricing rule…' : 'Updating pricing rule…');
    try {
      const response = mode === 'create'
        ? await AddServicePrices('', serviceId, data.pricetype, data.currency, data.unitprice, data.effectivefrom, data.effectiveto)
        : await UpdateServicePrices('', priceId, serviceId, data.pricetype, data.currency, data.unitprice, data.effectivefrom, data.effectiveto);
      if (response?.status === 'success') {
        closePriceModal();
        await fetchDashboardData();
        showSuccess(mode === 'create' ? 'Pricing rule added successfully.' : 'Pricing rule updated successfully.');
      }
    } catch (error) {
      console.error('Error saving price:', error);
      showApiError(error, 'The pricing rule could not be saved.');
    } finally {
      endMutation();
    }
  };

  const deletePrice = async (id) => {
    if (mutation.key) return;
    beginMutation('price-delete', 'Deleting pricing rule…');
    try {
      const response = await DeleteServicePrices('', id);
      if (response?.status === 'success') {
        await fetchDashboardData();
        showSuccess('Pricing rule deleted successfully.');
      }
    } catch (error) {
      console.error('Error deleting price:', error);
      showApiError(error, 'The pricing rule could not be deleted.');
    } finally {
      endMutation();
    }
  };

  const filteredServices = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return services;
    return services.filter(service => [service.name, service.description, service.unitlabel].some(value => value.toLowerCase().includes(query)));
  }, [services, search]);
  const totalPrices = useMemo(() => services.reduce((total, service) => total + service.prices.length, 0), [services]);
  const meteredCount = useMemo(() => services.filter(service => service.metered === '1').length, [services]);
  const visibleServices = filteredServices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const filteredPackages = useMemo(() => {
    const query = packageSearch.trim().toLowerCase();
    if (!query) return packages;
    return packages.filter((item) => {
      const serviceNames = item.serviceIds
        .map(id => services.find(service => service.id === id)?.name || '')
        .join(' ');
      const planNames = item.plans.map(plan => plan.name || '').join(' ');
      return `${item.name} ${item.description} ${serviceNames} ${planNames}`.toLowerCase().includes(query);
    });
  }, [packageSearch, packages, services]);
  const visiblePackages = filteredPackages.slice(
    packagePage * packagesPerPage,
    packagePage * packagesPerPage + packagesPerPage
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredPackages.length / packagesPerPage) - 1);
    if (packagePage > lastPage) setPackagePage(lastPage);
  }, [filteredPackages.length, packagePage, packagesPerPage]);

  const updateServiceField = (field, value) => setServiceModal(previous => ({ ...previous, data: { ...previous.data, [field]: value } }));
  const updatePriceField = (field, value) => setPriceModal(previous => ({ ...previous, data: { ...previous.data, [field]: value } }));
  const openNewService = () => setServiceModal({ open: true, mode: 'create', data: { ...emptyService } });
  const openEditService = (service) => setServiceModal({ open: true, mode: 'edit', data: { ...service } });
  const openNewPrice = (serviceId) => setPriceModal({ open: true, mode: 'create', serviceId, priceId: null, data: { ...emptyPrice } });
  const openEditPrice = (serviceId, price) => setPriceModal({
    open: true, mode: 'edit', serviceId, priceId: price.id,
    data: { ...price, effectivefrom: price.effectivefrom === '—' ? '' : price.effectivefrom, effectiveto: price.effectiveto === '—' ? '' : price.effectiveto }
  });
  const openNewPackage = () => setPackageDrawer({ open: true, mode: 'create', packageId: null, data: { ...emptyPackage, serviceIds: [] } });
  const openEditPackage = (item, mode = 'edit') => setPackageDrawer({
    open: true,
    mode,
    packageId: item.id,
    data: { name: item.name, description: item.description, serviceIds: [...item.serviceIds] }
  });
  const openPlansDrawer = (packageId) => setPlansDrawer({ open: true, packageId });
  const openNewPlan = (packageId) => {
    closePlansDrawer();
    setPlanDrawer({ open: true, mode: 'create', packageId, planId: null, data: { ...emptyPlan } });
  };
  const openEditPlan = (packageId, plan) => {
    closePlansDrawer();
    setPlanDrawer({ open: true, mode: 'edit', packageId, planId: plan.id, data: { ...plan } });
  };
  const updatePackageField = (field, value) => setPackageDrawer(previous => ({ ...previous, data: { ...previous.data, [field]: value } }));
  const updatePlanField = (field, value) => setPlanDrawer(previous => ({ ...previous, data: { ...previous.data, [field]: value } }));
  const togglePackageService = (serviceId) => setPackageDrawer(previous => ({
    ...previous,
    data: {
      ...previous.data,
      serviceIds: previous.data.serviceIds.includes(serviceId)
        ? previous.data.serviceIds.filter(id => id !== serviceId)
        : [...previous.data.serviceIds, serviceId]
    }
  }));
  const savePackage = async () => {
    if (mutation.key) return;
    const { mode, packageId, data } = packageDrawer;
    beginMutation(
      'package-save',
      mode === 'create'
        ? 'Creating package…'
        : mode === 'services'
          ? 'Updating package services…'
          : 'Updating package…'
    );
    try {
      const response = mode === 'create'
        ? await AddPackage(data.name, data.description, data.serviceIds)
        : mode === 'services'
          ? await UpdatePackageServices(packageId, data.serviceIds)
          : await UpdatePackage(packageId, data.name, data.description, data.serviceIds);
      if (response?.status === 'success') {
        closePackageDrawer();
        await getPackages();
        showSuccess(
          mode === 'create'
            ? 'Package created successfully.'
            : mode === 'services'
              ? 'Package services updated successfully.'
              : 'Package updated successfully.'
        );
      }
    } catch (error) {
      console.error('Package API Error:', error);
      showApiError(error, 'The package could not be saved.');
    } finally {
      endMutation();
    }
  };
  const deletePackage = async (packageId) => {
    if (mutation.key) return;
    beginMutation('package-delete', 'Deleting package…');
    try {
      const response = await DeletePackage(packageId);
      if (response?.status === 'success') {
        await getPackages();
        showSuccess('Package deleted successfully.');
      }
    } catch (error) {
      console.error('Package API Error:', error);
      showApiError(error, 'The package could not be deleted.');
    } finally {
      endMutation();
    }
  };
  const savePlan = async () => {
    if (mutation.key) return;
    const { mode, packageId, planId, data } = planDrawer;
    beginMutation('plan-save', mode === 'create' ? 'Creating plan…' : 'Updating plan…');
    const payload = {
      package_id: packageId,
      name: data.name,
      currency: data.currency,
      billing_interval_months: Number(data.billingIntervalMonths),
      price_recurring: data.price,
      active: data.active === '1',
      effective_from: data.effectivefrom,
      effective_to: data.effectiveto || null,
      default_new_charge_mode: data.newChargeMode,
      default_old_credit_mode: data.oldCreditMode
    };
    try {
      const response = mode === 'create'
        ? await AddPlan(payload)
        : await UpdatePlan(planId, payload);
      if (response?.status === 'success') {
        closePlanDrawer();
        await getPackages();
        showSuccess(mode === 'create' ? 'Plan created successfully.' : 'Plan updated successfully.');
      }
    } catch (error) {
      console.error('Plan API Error:', error);
      showApiError(error, 'The plan could not be saved.');
    } finally {
      endMutation();
    }
  };
  const deletePlan = async (planId) => {
    if (mutation.key) return;
    beginMutation('plan-delete', 'Deleting plan…');
    try {
      const response = await DeletePlan(planId);
      if (response?.status === 'success') {
        await getPackages();
        showSuccess('Plan deleted successfully.');
      }
    } catch (error) {
      console.error('Plan API Error:', error);
      showApiError(error, 'The plan could not be deleted.');
    } finally {
      endMutation();
    }
  };
  const formatPrice = (price) => `${price.currency || 'USD'} ${Number(price.unitprice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const selectedPlansPackage = packages.find(item => item.id === plansDrawer.packageId);
  const renderPackageCard = (item) => (
    <article className="new-fees-package-card" key={item.id} role="listitem">
      <div className="new-fees-package-card-heading">
        <div>
          <span className="new-fees-package-icon"><PackageIcon /></span>
          <div><span className="new-fees-eyebrow">Package</span><h3 title={item.name}>{item.name}</h3></div>
        </div>
        <div className="new-fees-package-actions">
          <Tooltip title="Manage services"><IconButton className="new-fees-manage-services" aria-label={`Manage services for ${item.name}`} onClick={() => openEditPackage(item, 'services')} size="small"><ServicesIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Edit package"><IconButton aria-label={`Edit ${item.name}`} onClick={() => openEditPackage(item)} size="small"><EditIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Delete package"><IconButton className="new-fees-delete" aria-label={`Delete ${item.name}`} onClick={() => confirmDelete('Delete this package? Packages with plans cannot be deleted.', () => deletePackage(item.id))} size="small"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        </div>
      </div>
      <p className="new-fees-package-description" title={item.description || 'No description provided.'}>{item.description || 'No description provided.'}</p>
      <div className="new-fees-included-services">
        <span>Included services</span>
        <div>{item.serviceIds.map(id => services.find(service => service.id === id)).filter(Boolean).map(service => <Chip key={service.id} label={service.name} size="small" />)}</div>
      </div>
      <div className="new-fees-plans">
        <div className="new-fees-plans-heading">
          <span>Plans & package pricing</span>
          <div><small>{item.plans.length} {item.plans.length === 1 ? 'plan' : 'plans'}</small><Button className="new-fees-plan-add" startIcon={<AddIcon />} onClick={() => openNewPlan(item.id)}>Add plan</Button></div>
        </div>
        {item.plans.length ? <>
          {item.plans.slice(0, 2).map(plan => <div className="new-fees-plan-row" key={plan.id}><PlanIcon fontSize="small" /><div className="new-fees-plan-details"><strong>{plan.name || 'Package plan'}{plan.active === '0' && <span>Inactive</span>}</strong><small>{plan.billingIntervalMonths} month billing · {plan.effectivefrom || 'Start date not set'} → {plan.effectiveto || 'No end date'}</small></div><b>{plan.currency} {Number(plan.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></div>)}
          <Button className="new-fees-view-plans" onClick={() => openPlansDrawer(item.id)}>{item.plans.length > 2 ? `View all ${item.plans.length} plans` : 'View plan details'}</Button>
        </> : <div className="new-fees-no-plans">No plans yet. Add a dated package price to get started.</div>}
      </div>
    </article>
  );

  return (
    <Box className="new-fees-page">
      <Backdrop className="new-fees-mutation-loader" open={Boolean(mutation.key)} role="status" aria-live="polite" aria-busy={Boolean(mutation.key)} aria-label={mutation.label || 'Processing request'}>
        <CircularProgress size={34} thickness={4.5} />
        <strong>{mutation.label}</strong>
        <span>Please wait while your changes are saved.</span>
      </Backdrop>
      <section className="new-fees-hero">
        <div>
          <span className="new-fees-eyebrow">Commercial operations</span>
          <h1>{activeWorkspace === 'services' ? 'Service pricing' : 'Packages and plans'}</h1>
          <p>{activeWorkspace === 'services' ? 'Manage the services your business offers and the pricing rules that make them billable.' : 'Bundle services into commercial packages and define time-bound plan prices.'}</p>
        </div>
        <Button className="new-fees-primary-action" variant="contained" disableElevation startIcon={<AddIcon />} onClick={activeWorkspace === 'services' ? openNewService : openNewPackage}>{activeWorkspace === 'services' ? 'New service' : 'New package'}</Button>
      </section>

      <nav className="new-fees-workspace-switch" aria-label="Fees management workspaces">
        <button type="button" className={activeWorkspace === 'services' ? 'active' : ''} onClick={() => setActiveWorkspace('services')}><ServicesIcon fontSize="small" />Services & pricing</button>
        <button type="button" className={activeWorkspace === 'packages' ? 'active' : ''} onClick={() => setActiveWorkspace('packages')}><PackageIcon fontSize="small" />Packages & plans</button>
      </nav>

      {activeWorkspace === 'services' && <section className="new-fees-summary" aria-label="Pricing summary">
        <article><span>Configured services</span><strong>{loading ? '—' : services.length}</strong><small>Available to price and bill</small></article>
        <article className="dark"><span>Pricing rules</span><strong>{loading ? '—' : totalPrices}</strong><small>Active rate configurations</small></article>
        <article className="accent"><span>Metered services</span><strong>{loading ? '—' : meteredCount}</strong><small>Usage-based offerings</small></article>
      </section>}

      {activeWorkspace === 'services' && <section className="new-fees-workspace">
        <div className="new-fees-workspace-heading">
          <div><span className="new-fees-eyebrow">Service catalogue</span><h2>Services and rate cards</h2></div>
          <TextField className="new-fees-search" placeholder="Search services" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} size="small" inputProps={{ 'aria-label': 'Search services' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        </div>

        <TableContainer className="new-fees-table-wrap">
          <Table className="new-fees-table" aria-label="Services and pricing">
            <TableHead><TableRow><TableCell /><TableCell>Service</TableCell><TableCell>Description</TableCell><TableCell>Billing unit</TableCell><TableCell>Model</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="new-fees-state"><CircularProgress size={22} /><span>Loading your service catalogue…</span></TableCell></TableRow>}
              {!loading && visibleServices.map(service => {
                const expanded = expandedServiceId === service.id;
                return <React.Fragment key={service.id}>
                  <TableRow className={expanded ? 'is-expanded' : ''}>
                    <TableCell className="new-fees-expand-cell"><IconButton aria-label={`View pricing for ${service.name}`} onClick={() => setExpandedServiceId(expanded ? null : service.id)} size="small">{expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</IconButton></TableCell>
                    <TableCell><div className="new-fees-service-name"><span className="new-fees-service-icon"><ServicesIcon fontSize="small" /></span><strong>{service.name}</strong><small>{service.prices.length} {service.prices.length === 1 ? 'rate' : 'rates'}</small></div></TableCell>
                    <TableCell className="new-fees-description">{service.description || 'No description provided'}</TableCell>
                    <TableCell><Chip className="new-fees-unit-chip" label={service.unitlabel} size="small" /></TableCell>
                    <TableCell><Chip className={service.metered === '1' ? 'new-fees-metered-chip' : 'new-fees-static-chip'} label={service.metered === '1' ? 'Metered' : 'Fixed'} size="small" /></TableCell>
                    <TableCell align="right"><div className="new-fees-actions"><Tooltip title="Edit service"><IconButton aria-label={`Edit ${service.name}`} onClick={() => openEditService(service)} size="small"><EditIcon fontSize="small" /></IconButton></Tooltip><Tooltip title="Delete service"><IconButton aria-label={`Delete ${service.name}`} className="new-fees-delete" onClick={() => confirmDelete('Delete this service and its associated pricing?', () => deleteService(service.id))} size="small"><DeleteIcon fontSize="small" /></IconButton></Tooltip></div></TableCell>
                  </TableRow>
                  <TableRow className="new-fees-detail-row"><TableCell colSpan={6}><Collapse in={expanded} timeout="auto" unmountOnExit><div className="new-fees-rates"><div className="new-fees-rates-heading"><div><span className="new-fees-eyebrow">Rate card</span><h3>{service.name} pricing</h3></div><Button className="new-fees-secondary-action" variant="outlined" startIcon={<AddIcon />} onClick={() => openNewPrice(service.id)}>Add rate</Button></div>
                    {service.prices.length ? <div className="new-fees-rate-grid">{service.prices.map(price => <article className="new-fees-rate-card" key={price.id}><div><span>{price.pricetype?.replace('_', ' ') || 'On demand'}</span><strong>{formatPrice(price)}</strong><small>{price.effectivefrom === '—' ? 'Effective immediately' : `From ${price.effectivefrom}`}{price.effectiveto !== '—' ? ` · Until ${price.effectiveto}` : ''}</small></div><div className="new-fees-actions"><Tooltip title="Edit rate"><IconButton aria-label={`Edit ${formatPrice(price)} rate`} onClick={() => openEditPrice(service.id, price)} size="small"><EditIcon fontSize="small" /></IconButton></Tooltip><Tooltip title="Delete rate"><IconButton aria-label={`Delete ${formatPrice(price)} rate`} className="new-fees-delete" onClick={() => confirmDelete('Delete this pricing rule?', () => deletePrice(price.id))} size="small"><DeleteIcon fontSize="small" /></IconButton></Tooltip></div></article>)}</div> : <div className="new-fees-empty-rates"><PaidIcon /><div><strong>No pricing rules yet</strong><span>Create a rate card to make this service ready for billing.</span></div><Button variant="text" onClick={() => openNewPrice(service.id)}>Add first rate</Button></div>}</div></Collapse></TableCell></TableRow>
                </React.Fragment>;
              })}
              {!loading && !visibleServices.length && <TableRow><TableCell colSpan={6} className="new-fees-empty"><ServicesIcon /><strong>{search ? 'No matching services' : 'Your service catalogue is empty'}</strong><span>{search ? 'Try a different keyword.' : 'Create your first service to begin setting up pricing.'}</span>{!search && <Button variant="outlined" onClick={openNewService}>Create service</Button>}</TableCell></TableRow>}
            </TableBody>
          </Table>
          {!loading && filteredServices.length > 0 && <TablePagination component="div" count={filteredServices.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, newPage) => setPage(newPage)} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[5, 10, 25]} />}
        </TableContainer>
      </section>}

      {activeWorkspace === 'packages' && <section className="new-fees-package-workspace">
        <div className="new-fees-package-heading"><div><span className="new-fees-eyebrow">Package catalogue</span><h2>Build packages from your services</h2><p>Each package can include one or more services and can have multiple dated plan prices.</p></div><span className="new-fees-ui-only">Packages & plans connected</span></div>
        {!packages.length && <div className="new-fees-package-empty"><PackageIcon /><h3>No packages created yet</h3><p>Create a package, select the included services, then add as many pricing plans as you need.</p><Button className="new-fees-primary-action" variant="contained" disableElevation startIcon={<AddIcon />} onClick={openNewPackage}>Create package</Button></div>}
        {!!packages.length && <>
          <div className="new-fees-package-toolbar">
            <div className="new-fees-package-results" aria-live="polite"><strong>{filteredPackages.length}</strong> {filteredPackages.length === 1 ? 'package' : 'packages'}</div>
            <div className="new-fees-package-toolbar-controls">
              <TextField className="new-fees-package-search" placeholder="Search packages" value={packageSearch} onChange={(event) => { setPackageSearch(event.target.value); setPackagePage(0); }} size="small" inputProps={{ 'aria-label': 'Search packages' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              <div className="new-fees-view-toggle" role="group" aria-label="Package layout">
                <Tooltip title="Grid view"><IconButton aria-label="Show packages in grid view" aria-pressed={packageView === 'grid'} className={packageView === 'grid' ? 'active' : ''} onClick={() => { setPackageView('grid'); setPackagePage(0); }}><GridViewIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="List view"><IconButton aria-label="Show packages in list view" aria-pressed={packageView === 'list'} className={packageView === 'list' ? 'active' : ''} onClick={() => { setPackageView('list'); setPackagePage(0); }}><ListViewIcon fontSize="small" /></IconButton></Tooltip>
              </div>
            </div>
          </div>
          {filteredPackages.length ? <div className={`new-fees-package-grid ${packageView === 'list' ? 'is-list' : 'is-grid'}`} role="list" aria-label={`Packages in ${packageView} view`}>{visiblePackages.map(renderPackageCard)}</div> : <div className="new-fees-package-no-results"><SearchIcon /><strong>No matching packages</strong><span>Try searching by package, service, or plan name.</span><Button variant="text" onClick={() => setPackageSearch('')}>Clear search</Button></div>}
          {!!filteredPackages.length && <TablePagination className="new-fees-package-pagination" component="div" count={filteredPackages.length} page={packagePage} rowsPerPage={packagesPerPage} onPageChange={(_, newPage) => setPackagePage(newPage)} onRowsPerPageChange={(event) => { setPackagesPerPage(Number(event.target.value)); setPackagePage(0); }} rowsPerPageOptions={[4, 8, 12, 24]} labelRowsPerPage="Packages per page" />}
        </>}
      </section>}

      <Drawer anchor="right" open={serviceModal.open} onClose={closeServiceModal} PaperProps={{ className: 'new-fees-drawer' }}>
        <div className="new-fees-drawer-content"><div className="new-fees-drawer-heading"><div><span className="new-fees-eyebrow">Service catalogue</span><h2>{serviceModal.mode === 'create' ? 'Create a new service' : 'Edit service'}</h2><p>Set up the core details used to identify and bill this offering.</p></div><IconButton aria-label="Close drawer" onClick={closeServiceModal}><CloseIcon /></IconButton></div>
          <div className="new-fees-form-grid"><TextField autoFocus label="Service name" value={serviceModal.data.name} onChange={(event) => updateServiceField('name', event.target.value)} required fullWidth /><TextField label="Description" value={serviceModal.data.description} onChange={(event) => updateServiceField('description', event.target.value)} multiline rows={3} fullWidth /><div className="new-fees-two-column"><TextField label="Billing unit" placeholder="e.g. hour, user, item" value={serviceModal.data.unitlabel} onChange={(event) => updateServiceField('unitlabel', event.target.value)} fullWidth /><TextField select label="Billing model" value={serviceModal.data.metered} onChange={(event) => updateServiceField('metered', event.target.value)} fullWidth><MenuItem value="1">Metered</MenuItem><MenuItem value="0">Fixed</MenuItem></TextField></div></div>
          <div className="new-fees-drawer-actions"><Button disabled={Boolean(mutation.key)} onClick={closeServiceModal}>Cancel</Button><Button className="new-fees-primary-action" variant="contained" disableElevation disabled={Boolean(mutation.key) || !serviceModal.data.name.trim() || !serviceModal.data.unitlabel.trim()} onClick={saveService}>{mutation.key === 'service-save' ? <><CircularProgress className="new-fees-button-spinner" size={16} />Saving…</> : serviceModal.mode === 'create' ? 'Create service' : 'Save changes'}</Button></div>
        </div>
      </Drawer>

      <Drawer anchor="right" open={priceModal.open} onClose={closePriceModal} PaperProps={{ className: 'new-fees-drawer' }}>
        <div className="new-fees-drawer-content"><div className="new-fees-drawer-heading"><div><span className="new-fees-eyebrow">Rate card</span><h2>{priceModal.mode === 'create' ? 'Add a pricing rule' : 'Edit pricing rule'}</h2><p>Define the price and period this rate should apply to.</p></div><IconButton aria-label="Close drawer" onClick={closePriceModal}><CloseIcon /></IconButton></div>
          <div className="new-fees-form-grid"><div className="new-fees-two-column"><TextField select label="Price type" value={priceModal.data.pricetype} onChange={(event) => updatePriceField('pricetype', event.target.value)} fullWidth><MenuItem value="on_demand">On demand</MenuItem></TextField><TextField label="Currency" value={priceModal.data.currency} onChange={(event) => updatePriceField('currency', event.target.value.toUpperCase())} inputProps={{ maxLength: 3 }} fullWidth /></div><TextField label="Unit price" type="number" value={priceModal.data.unitprice} onChange={(event) => updatePriceField('unitprice', event.target.value)} inputProps={{ min: '0', step: '0.01' }} fullWidth /><div className="new-fees-two-column"><TextField label="Effective from" type="date" value={priceModal.data.effectivefrom} onChange={(event) => updatePriceField('effectivefrom', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth /><TextField label="Effective to" type="date" value={priceModal.data.effectiveto} onChange={(event) => updatePriceField('effectiveto', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></div></div>
          <div className="new-fees-drawer-actions"><Button disabled={Boolean(mutation.key)} onClick={closePriceModal}>Cancel</Button><Button className="new-fees-primary-action" variant="contained" disableElevation disabled={Boolean(mutation.key) || priceModal.data.unitprice === ''} onClick={savePrice}>{mutation.key === 'price-save' ? <><CircularProgress className="new-fees-button-spinner" size={16} />Saving…</> : priceModal.mode === 'create' ? 'Add rate' : 'Save changes'}</Button></div>
        </div>
      </Drawer>

      <Drawer anchor="right" open={packageDrawer.open} onClose={closePackageDrawer} PaperProps={{ className: 'new-fees-drawer new-fees-package-drawer' }}>
        <div className="new-fees-drawer-content"><div className="new-fees-drawer-heading"><div><span className="new-fees-eyebrow">Package catalogue</span><h2>{packageDrawer.mode === 'create' ? 'Create a package' : packageDrawer.mode === 'services' ? 'Manage package services' : 'Edit package'}</h2><p>{packageDrawer.mode === 'services' ? 'Add or remove the services included in this package.' : 'Group one or more existing services into a package ready for plan pricing.'}</p></div><IconButton aria-label="Close drawer" onClick={closePackageDrawer}><CloseIcon /></IconButton></div><div className="new-fees-form-grid">{packageDrawer.mode !== 'services' && <><TextField autoFocus label="Package name" value={packageDrawer.data.name} onChange={(event) => updatePackageField('name', event.target.value)} required fullWidth /><TextField label="Description" value={packageDrawer.data.description} onChange={(event) => updatePackageField('description', event.target.value)} multiline rows={3} fullWidth /></>}<div className="new-fees-service-picker"><div><span className="new-fees-eyebrow">Included services</span><strong>Select one or more services</strong><p>Services retain their own rates; package plans define the package price.</p></div>{services.length ? services.map(service => <FormControlLabel key={service.id} className="new-fees-service-option" control={<Checkbox checked={packageDrawer.data.serviceIds.includes(service.id)} onChange={() => togglePackageService(service.id)} />} label={<span><b>{service.name}</b><small>{service.unitlabel} · {service.metered === '1' ? 'Metered' : 'Fixed'}</small></span>} />) : <div className="new-fees-no-services">Create a service first, then return to add it to this package.</div>}</div></div><div className="new-fees-drawer-actions"><Button disabled={Boolean(mutation.key)} onClick={closePackageDrawer}>Cancel</Button><Button className="new-fees-primary-action" variant="contained" disableElevation disabled={Boolean(mutation.key) || !packageDrawer.data.name.trim() || !packageDrawer.data.serviceIds.length} onClick={savePackage}>{mutation.key === 'package-save' ? <><CircularProgress className="new-fees-button-spinner" size={16} />Saving…</> : packageDrawer.mode === 'create' ? 'Create package' : packageDrawer.mode === 'services' ? 'Save services' : 'Save changes'}</Button></div></div>
      </Drawer>

      <Drawer anchor="right" open={plansDrawer.open} onClose={closePlansDrawer} PaperProps={{ className: 'new-fees-drawer new-fees-plans-drawer' }}>
        <div className="new-fees-drawer-content">
          <div className="new-fees-drawer-heading">
            <div><span className="new-fees-eyebrow">Package plans</span><h2>{selectedPlansPackage?.name || 'Plans'}</h2><p>Review pricing periods and billing behavior for every plan in this package.</p></div>
            <IconButton aria-label="Close plans drawer" onClick={closePlansDrawer}><CloseIcon /></IconButton>
          </div>
          <div className="new-fees-plan-list-toolbar">
            <div><strong>{selectedPlansPackage?.plans.length || 0} {(selectedPlansPackage?.plans.length || 0) === 1 ? 'plan' : 'plans'}</strong><span>Prices are shown in effective-date order.</span></div>
            <Button className="new-fees-primary-action" variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => openNewPlan(selectedPlansPackage?.id)}>Add plan</Button>
          </div>
          <div className="new-fees-plan-detail-list">
            {selectedPlansPackage?.plans.map(plan => <article className="new-fees-plan-detail-card" key={plan.id}>
              <div className="new-fees-plan-detail-heading">
                <div><span className="new-fees-plan-detail-icon"><PlanIcon /></span><div><strong>{plan.name}</strong><Chip size="small" label={plan.active === '1' ? 'Active' : 'Inactive'} className={plan.active === '1' ? 'is-active' : 'is-inactive'} /></div></div>
                <b>{plan.currency} {Number(plan.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
              </div>
              <div className="new-fees-plan-facts">
                <div><span>Effective period</span><strong>{plan.effectivefrom || 'Not set'} → {plan.effectiveto || 'No end date'}</strong></div>
                <div><span>Billing interval</span><strong>{plan.billingIntervalMonths === '1' ? 'Every month' : `Every ${plan.billingIntervalMonths} months`}</strong></div>
                <div><span>New plan charge</span><strong>{plan.newChargeMode === 'full' ? 'Full charge' : 'Prorated charge'}</strong></div>
                <div><span>Old plan credit</span><strong>{plan.oldCreditMode === 'full' ? 'Full credit' : 'Prorated credit'}</strong></div>
              </div>
              <div className="new-fees-plan-detail-actions">
                <Button startIcon={<EditIcon />} onClick={() => openEditPlan(selectedPlansPackage.id, plan)}>Edit plan</Button>
                <Button className="new-fees-delete" startIcon={<DeleteIcon />} onClick={() => confirmDelete('Delete this plan? Plans with customer subscriptions cannot be deleted.', () => deletePlan(plan.id))}>Delete</Button>
              </div>
            </article>)}
            {selectedPlansPackage && !selectedPlansPackage.plans.length && <div className="new-fees-plan-detail-empty"><PlanIcon /><strong>No plans yet</strong><span>Add the first dated price for this package.</span><Button variant="outlined" startIcon={<AddIcon />} onClick={() => openNewPlan(selectedPlansPackage.id)}>Add plan</Button></div>}
          </div>
        </div>
      </Drawer>

      <Drawer anchor="right" open={planDrawer.open} onClose={closePlanDrawer} PaperProps={{ className: 'new-fees-drawer' }}>
        <div className="new-fees-drawer-content"><div className="new-fees-drawer-heading"><div><span className="new-fees-eyebrow">Package plan</span><h2>{planDrawer.mode === 'create' ? 'Add a plan price' : 'Edit plan price'}</h2><p>Set the package price, billing behavior, and effective period.</p></div><IconButton aria-label="Close drawer" onClick={closePlanDrawer}><CloseIcon /></IconButton></div><div className="new-fees-form-grid"><TextField autoFocus label="Plan name" placeholder="e.g. Annual package rate" value={planDrawer.data.name} onChange={(event) => updatePlanField('name', event.target.value)} required fullWidth /><div className="new-fees-two-column"><TextField label="Currency" value={planDrawer.data.currency} onChange={(event) => updatePlanField('currency', event.target.value.toUpperCase())} inputProps={{ maxLength: 3 }} fullWidth /><TextField label="Package price" type="number" value={planDrawer.data.price} onChange={(event) => updatePlanField('price', event.target.value)} inputProps={{ min: '0', step: '0.01' }} fullWidth /></div><div className="new-fees-two-column"><TextField label="Billing interval (months)" type="number" value={planDrawer.data.billingIntervalMonths} onChange={(event) => updatePlanField('billingIntervalMonths', event.target.value)} inputProps={{ min: '1', max: '120', step: '1' }} fullWidth /><TextField select label="Status" value={planDrawer.data.active} onChange={(event) => updatePlanField('active', event.target.value)} fullWidth><MenuItem value="1">Active</MenuItem><MenuItem value="0">Inactive</MenuItem></TextField></div><div className="new-fees-two-column"><TextField label="Effective from" type="date" value={planDrawer.data.effectivefrom} onChange={(event) => updatePlanField('effectivefrom', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth /><TextField label="Effective to" type="date" value={planDrawer.data.effectiveto} onChange={(event) => updatePlanField('effectiveto', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></div><div className="new-fees-two-column"><TextField select label="New charge mode" value={planDrawer.data.newChargeMode} onChange={(event) => updatePlanField('newChargeMode', event.target.value)} fullWidth><MenuItem value="prorated">Prorated</MenuItem><MenuItem value="full">Full</MenuItem></TextField><TextField select label="Old credit mode" value={planDrawer.data.oldCreditMode} onChange={(event) => updatePlanField('oldCreditMode', event.target.value)} fullWidth><MenuItem value="prorated">Prorated</MenuItem><MenuItem value="full">Full</MenuItem></TextField></div></div><div className="new-fees-drawer-actions"><Button disabled={Boolean(mutation.key)} onClick={closePlanDrawer}>Cancel</Button><Button className="new-fees-primary-action" variant="contained" disableElevation disabled={Boolean(mutation.key) || !planDrawer.data.name.trim() || planDrawer.data.price === '' || !planDrawer.data.billingIntervalMonths || !planDrawer.data.effectivefrom} onClick={savePlan}>{mutation.key === 'plan-save' ? <><CircularProgress className="new-fees-button-spinner" size={16} />Saving…</> : planDrawer.mode === 'create' ? 'Create plan' : 'Save changes'}</Button></div></div>
      </Drawer>

      <Snackbar
        className="new-fees-snackbar"
        open={alert.showAlert}
        autoHideDuration={alert.severity === 'confirm' ? null : 4500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={closeAlert}
      >
        <Alert
          className={`new-fees-snackbar-alert ${alert.severity === 'confirm' ? 'is-confirmation' : ''}`}
          severity={alert.severity === 'confirm' ? 'warning' : alert.severity || 'info'}
          variant="filled"
          onClose={alert.severity === 'confirm' ? undefined : closeAlert}
          action={alert.severity === 'confirm' ? <div className="new-fees-snackbar-actions"><Button onClick={closeAlert}>Cancel</Button><Button variant="contained" onClick={confirmAlertAction}>Confirm</Button></div> : undefined}
        >
          {alert.header && <AlertTitle>{alert.header}</AlertTitle>}
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
